import { useMemo, useState } from 'react'
import { useParams, useNavigate, Link } from '@tanstack/react-router'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { Textarea } from './ui/textarea'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card'
import { mdiArrowLeft, mdiContentSave } from '@mdi/js'
import { AppIcon } from './ui/icon'
import { useContractStakeholderTypes, useCreateContractStakeholderType } from '../hooks/useQueries'
import { useBreadcrumbs } from '../hooks/useBreadcrumbs'
import { useToast } from '../hooks/use-toast'
import { PageHeader } from './shared/PageHeader'
import { SECTION_ICONS } from '../lib/section-icons'

export function CreateContractStakeholderTypePage() {
  const { org } = useParams({ strict: false })
  const navigate = useNavigate()
  const { toast } = useToast()

  const { data: existingStakeholderTypes = [] } = useContractStakeholderTypes(org || 'demo')
  const createMutation = useCreateContractStakeholderType(org || 'demo')

  // State for form fields
  const [code, setCode] = useState('')
  const [nameEn, setNameEn] = useState('')
  const [nameFr, setNameFr] = useState('')
  const [descriptionEn, setDescriptionEn] = useState('')
  const [descriptionFr, setDescriptionFr] = useState('')

  // Set breadcrumbs
  const breadcrumbs = useMemo(() => [
    { title: 'Home', path: `/${org}` },
    { title: 'Org Settings', path: `/${org}/settings` },
    { title: 'Contract Stakeholder Types', path: `/${org}/contract-stakeholder-types` },
    { title: 'Create Stakeholder Type' }
  ], [org])

  useBreadcrumbs(breadcrumbs)

  // Validate code is unique
  const isCodeTaken = useMemo(() => {
    return existingStakeholderTypes.some(type => type.code.toLowerCase() === code.toLowerCase())
  }, [existingStakeholderTypes, code])

  const handleSave = async () => {
    // Validation
    if (!code.trim()) {
      toast({
        title: "Validation Error",
        description: "Code is required",
        variant: "destructive"
      })
      return
    }

    if (!nameEn.trim()) {
      toast({
        title: "Validation Error",
        description: "English name is required",
        variant: "destructive"
      })
      return
    }

    if (isCodeTaken) {
      toast({
        title: "Validation Error",
        description: "This code is already taken. Please choose a different one.",
        variant: "destructive"
      })
      return
    }

    const names: Record<string, string> = { en: nameEn }
    if (nameFr.trim()) names.fr = nameFr

    const description: Record<string, string> = {}
    if (descriptionEn.trim()) description.en = descriptionEn
    if (descriptionFr.trim()) description.fr = descriptionFr

    try {
      const result = await createMutation.mutateAsync({
        code: code.trim(),
        names,
        description: Object.keys(description).length > 0 ? description : undefined
      })

      toast({
        title: "Stakeholder type created",
        description: "The stakeholder type has been successfully created."
      })

      navigate({
        to: `/$org/contract-stakeholder-types/$contractStakeholderTypeUid`,
        params: {
          org: org || 'demo',
          contractStakeholderTypeUid: result.uid || ''
        }
      })
    } catch {
      toast({
        title: "Error",
        description: "Failed to create stakeholder type. Please try again.",
        variant: "destructive"
      })
    }
  }

  return (
    <div className="container mx-auto py-8 max-w-4xl">
      <div className="space-y-6">
        {/* Header */}
        <PageHeader
          icon={SECTION_ICONS.stakeholderTypes}
          title="Create Stakeholder Type"
          description="Create a new stakeholder type for your organization"
          actions={
            <Link to="/$org/contract-stakeholder-types" params={{ org: org || 'demo' }}>
              <Button variant="ghost">
                <AppIcon path={mdiArrowLeft} size="sm" className="mr-2" />
                Back
              </Button>
            </Link>
          }
        />

        {/* Form */}
        <Card>
          <CardHeader>
            <CardTitle>Stakeholder Type Details</CardTitle>
            <CardDescription>
              Enter the stakeholder type information. Names and descriptions support multiple languages.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Code */}
            <div className="space-y-2">
              <Label htmlFor="code">Code *</Label>
              <Input
                id="code"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ''))}
                placeholder="STAKEHOLDER_TYPE_CODE"
                data-testid="code-input"
              />
              <p className="text-xs text-muted-foreground">
                Unique identifier for the stakeholder type (uppercase letters, numbers, and underscores only)
              </p>
              {code && isCodeTaken && (
                <p className="text-xs text-destructive">This code is already taken</p>
              )}
            </div>

            {/* English Name */}
            <div className="space-y-2">
              <Label htmlFor="name-en">Name (English) *</Label>
              <Input
                id="name-en"
                value={nameEn}
                onChange={(e) => setNameEn(e.target.value)}
                placeholder="Stakeholder type name in English"
                data-testid="name-en-input"
              />
            </div>

            {/* French Name */}
            <div className="space-y-2">
              <Label htmlFor="name-fr">Name (French)</Label>
              <Input
                id="name-fr"
                value={nameFr}
                onChange={(e) => setNameFr(e.target.value)}
                placeholder="Stakeholder type name in French"
                data-testid="name-fr-input"
              />
            </div>

            {/* English Description */}
            <div className="space-y-2">
              <Label htmlFor="description-en">Description (English)</Label>
              <Textarea
                id="description-en"
                value={descriptionEn}
                onChange={(e) => setDescriptionEn(e.target.value)}
                placeholder="Stakeholder type description in English"
                rows={3}
                data-testid="description-en-input"
              />
            </div>

            {/* French Description */}
            <div className="space-y-2">
              <Label htmlFor="description-fr">Description (French)</Label>
              <Textarea
                id="description-fr"
                value={descriptionFr}
                onChange={(e) => setDescriptionFr(e.target.value)}
                placeholder="Stakeholder type description in French"
                rows={3}
                data-testid="description-fr-input"
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end pt-4 border-t">
              <Button
                onClick={handleSave}
                disabled={createMutation.isPending || !code.trim() || !nameEn.trim() || isCodeTaken}
                data-testid="create-stakeholder-type"
              >
                <AppIcon path={mdiContentSave} size="sm" className="mr-2" />
                Create Stakeholder Type
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}